import {
  Resource_AppointmentTaskContentType,
  Resource_AppointmentTaskResultType,
  Resource_AppointmentTaskStatus,
  useUpdateAppointmentTaskResultMutation,
} from '@/generated/graphql';
import { CheckCircleOutlined } from '@ant-design/icons';
import { AppointmentTaskComponent } from '@components/appointment-details/appointment-tasks/types';
import { AveliosButton } from '@components/common-layout/avelios-button';
import { Error } from '@components/error';
import { Form } from '@components/form';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { useCurrentUser } from '../../../../helpers/utils';
import { useCreateForm } from '../../../../hooks/use-create-form';

export const FormAppointmentTask: FC<AppointmentTaskComponent> = ({
  proxyAppointmentTask,
  updateAppointmentTaskStatus,
}) => {
  const { formatMessage } = useIntl();

  const [formRevisionGroupId, setFormRevisionGroupId] = useState<string>();

  const clinicId = proxyAppointmentTask.clinicId;

  const appointmentTask = proxyAppointmentTask?.appointmentTask;

  const done =
    appointmentTask?.status === Resource_AppointmentTaskStatus.AppointmentTaskStatusFinished;

  const formTemplateVersioningGroupId = appointmentTask?.contentReferences?.find(
    (ref) =>
      ref?.type ===
      Resource_AppointmentTaskContentType.AppointmentTaskContentTypeFormTemplateVersioningGroup,
  )?.referenceId;

  const resultFormRevisionGroupId = appointmentTask?.resultReferences?.find(
    (ref) => ref?.type === Resource_AppointmentTaskResultType.AppointmentTaskResultTypeForm,
  )?.referenceId;

  const { currentUser, userLoading } = useCurrentUser();

  const [createForm, { loading: createFormLoading }] = useCreateForm();
  const [updateAppointmentTaskResultMutation] = useUpdateAppointmentTaskResultMutation();

  if (!clinicId || !formTemplateVersioningGroupId) {
    return <Error />;
  }

  const handleFormCompletion = () => {
    updateAppointmentTaskStatus(Resource_AppointmentTaskStatus.AppointmentTaskStatusFinished);
  };

  const handleFormStart = () => {
    if (resultFormRevisionGroupId) {
      // continue form
      setFormRevisionGroupId(resultFormRevisionGroupId);
    } else {
      // create a new form

      const appointmentTaskId = appointmentTask.id;
      const portalUserId = currentUser?.id;

      if (appointmentTaskId && portalUserId) {
        createForm(
          clinicId,
          formTemplateVersioningGroupId,
          undefined,
          (createdFormRevisionGroupId) => {
            if (createdFormRevisionGroupId) {
              // mark the appointment task as started and add form id as result reference
              updateAppointmentTaskResultMutation({
                variables: {
                  clinicId,
                  appointmentTaskId,
                  portalUserId,
                  newResultReferences: [
                    {
                      referenceId: createdFormRevisionGroupId,
                      type: Resource_AppointmentTaskResultType.AppointmentTaskResultTypeForm,
                    },
                  ],
                },
              }).then((result) => {
                setFormRevisionGroupId(createdFormRevisionGroupId);
                if (result.data?.updateAppointmentTaskResult?.appointmentTask?.id) {
                  updateAppointmentTaskStatus(
                    Resource_AppointmentTaskStatus.AppointmentTaskStatusStarted,
                    false,
                  );
                }
              });
            }
          },
        );
      }
    }
  };

  const loading = userLoading || createFormLoading;

  return formRevisionGroupId ? (
    <Form
      formRevisionGroupId={formRevisionGroupId}
      clinicId={clinicId}
      onComplete={handleFormCompletion}
      onReturn={() => setFormRevisionGroupId(undefined)}
    />
  ) : (
    <div>
      {done ? (
        <AveliosButton
          loading={loading}
          style={{ color: 'white', backgroundColor: 'darkgreen' }}
          icon={<CheckCircleOutlined />}
        >
          {formatMessage({ id: 'appointment-task.form.form-completed' })}
        </AveliosButton>
      ) : (
        <AveliosButton loading={loading} type={'primary'} block onClick={handleFormStart}>
          {resultFormRevisionGroupId
            ? formatMessage({ id: 'appointment-task.form.continue-form' })
            : formatMessage({ id: 'appointment-task.form.start-form' })}
        </AveliosButton>
      )}
    </div>
  );
};
